import type { ChartAccount, LocaleCode, NormalBalance } from '@/api/models';

/**
 * A node in the account tree, keyed by `account.id`. Row keys MUST be the
 * stable id because `expandedKeys` is keyed by them.
 */
export type AccountTreeNode = {
    account: ChartAccount;
    /** 0 at the root, +1 per level — drives the per-depth indent. */
    depth: number;
    children: AccountTreeNode[];
    hasChildren: boolean;
    isArchived: boolean;
    /** True when this node directly matches the active search term. */
    isMatch: boolean;
    /**
     * Rolled-up balance in the fixed **debit-positive** convention (integer
     * tetri). Used only for aggregation into ancestors — never displayed.
     */
    signedTetri: number;
    /**
     * The balance to render (integer tetri), in the account's own natural
     * direction and contra-adjusted: a normal position is positive, an
     * abnormal net or a contra account reads negative (danger colour).
     */
    displayTetri: number;
};

export type BuildAccountTreeOptions = {
    search?: string;
    showArchived?: boolean;
    /** Active locale — accepted for call-site fidelity; search spans all locales. */
    locale?: LocaleCode;
};

export type BuildAccountTreeResult = {
    roots: AccountTreeNode[];
    /**
     * Ids of every group (header) node in the full archived-filtered tree —
     * the default `expandedKeys` so the whole chart is visible on load.
     */
    allGroupIds: string[];
    /**
     * The expansion the page opens with: the **top-level** (depth 0) headers
     * only. Expanding a class reveals its groups, so the initial view is
     * classes + groups with individual accounts collapsed — e.g. `2000` and
     * `2100` visible, `2110` hidden until its group is opened.
     *
     * Distinct from {@link allGroupIds}, which expands every level. The
     * standard Georgian chart is ~260 rows; opening all of them buries the
     * structure the codes exist to express.
     */
    defaultExpandedIds: string[];
    /**
     * Group ids to auto-expand for the current search so matches show in
     * context. Empty when not searching.
     */
    autoExpandIds: string[];
    /** Postable (leaf) accounts currently visible — drives the toolbar count. */
    visiblePostableCount: number;
    isSearching: boolean;
};

/**
 * Converts a natural, contra-adjusted display balance from the signed
 * (debit-positive) rollup.
 *
 * - `natural` flips the sign for credit-normal accounts so a normal position is
 *   positive.
 * - a **contra** account is then negated again so it reads as a reduction
 *   within its parent (e.g. accumulated depreciation shows negative under
 *   assets).
 *
 * Exported for unit testing.
 */
export const signedToDisplayTetri = (
    signedTetri: number,
    normalBalance: NormalBalance,
    isContra: boolean,
): number => {
    const natural = normalBalance === 'DEBIT' ? signedTetri : -signedTetri;
    return isContra ? -natural : natural;
};

/**
 * A row matches on its code or its (server-resolved) display name.
 *
 * The list ships one name, so search covers the language the user is reading in
 * rather than every localization. That is the same set they can see, which is
 * the behaviour a search box implies.
 */
const matchesSearch = (account: ChartAccount, term: string): boolean =>
    account.code.toLowerCase().includes(term) ||
    account.name.toLowerCase().includes(term);

/**
 * Sort sibling accounts by `code` ascending, numeric-aware ("2" before "10").
 *
 * This is deliberately **not** the server's order, which is name-then-code. A
 * chart of accounts is navigated by code — `1110, 1120, 1130` is the structure,
 * and alphabetical order is meaningless to anyone reading it. Names are also
 * localized, so ordering by them silently reshuffles the whole tree when the
 * user switches between Georgian and English: the same account moves, and
 * muscle memory breaks on every toggle. Sorting here, at every level, makes the
 * order stable across languages.
 */
const byCode = (a: ChartAccount, b: ChartAccount): number =>
    a.code.localeCompare(b.code, undefined, { numeric: true });

/**
 * Builds the account tree from the flat, balance-joined account list.
 *
 * Pure and side-effect free — the part most likely to regress, so it carries
 * its own tests. Steps:
 *
 * 1. Drop archived accounts unless `showArchived`; mark the rest.
 * 2. Nest by `parentId`, sorting siblings by `code` at every level.
 * 3. Roll balances up bottom-up in the signed (debit-positive) convention —
 *    a header uses its own balance when the source provides one, else the sum
 *    of its descendants — then derive each node's natural, contra-adjusted
 *    display balance.
 * 4. When searching, keep only matches plus their ancestors (context) and
 *    descendants, and report which groups to auto-expand.
 */
export const buildAccountTree = (
    accounts: ChartAccount[],
    { search = '', showArchived = false }: BuildAccountTreeOptions = {},
): BuildAccountTreeResult => {
    const term = search.trim().toLowerCase();
    const isSearching = term.length > 0;

    // 1. Archived filter.
    const visibleAccounts = showArchived
        ? accounts
        : accounts.filter((account) => !account.archived);

    // 2. Group children by parentId. An account whose parent is missing from
    // the visible set is treated as a root so it can't vanish.
    const visibleIds = new Set(visibleAccounts.map((account) => account.id));
    const childrenByParent = new Map<string | null, ChartAccount[]>();
    for (const account of visibleAccounts) {
        const parentKey =
            account.parentId && visibleIds.has(account.parentId)
                ? account.parentId
                : null;
        const bucket = childrenByParent.get(parentKey);
        if (bucket) {
            bucket.push(account);
        } else {
            childrenByParent.set(parentKey, [account]);
        }
    }

    const allGroupIds: string[] = [];
    const defaultExpandedIds: string[] = [];

    // 3. Recursively build nodes + roll balances up.
    const buildNode = (
        account: ChartAccount,
        depth: number,
    ): AccountTreeNode => {
        const childAccounts = (childrenByParent.get(account.id) ?? [])
            .slice()
            .sort(byCode);
        const children = childAccounts.map((child) =>
            buildNode(child, depth + 1),
        );
        const hasChildren = children.length > 0;

        if (hasChildren) {
            allGroupIds.push(account.id);
            if (depth === 0) {
                defaultExpandedIds.push(account.id);
            }
        }

        // Header: prefer a source-provided balance, else sum descendants.
        // Leaf: its own balance (0 when it has no postings).
        let signedTetri: number;
        if (hasChildren) {
            signedTetri = account.balance
                ? account.balance.signedBalanceTetri
                : children.reduce((sum, child) => sum + child.signedTetri, 0);
        } else {
            signedTetri = account.balance?.signedBalanceTetri ?? 0;
        }

        return {
            account,
            depth,
            children,
            hasChildren,
            isArchived: account.archived,
            isMatch: isSearching && matchesSearch(account, term),
            signedTetri,
            displayTetri: signedToDisplayTetri(
                signedTetri,
                account.normalBalance,
                account.isContra,
            ),
        };
    };

    const roots = (childrenByParent.get(null) ?? [])
        .slice()
        .sort(byCode)
        .map((account) => buildNode(account, 0));

    // 4. No search → whole chart; count all postable.
    if (!isSearching) {
        return {
            roots,
            allGroupIds,
            defaultExpandedIds,
            autoExpandIds: [],
            visiblePostableCount: countPostable(roots),
            isSearching: false,
        };
    }

    // Search → prune to matches + ancestors + descendants.
    const prune = (
        node: AccountTreeNode,
        ancestorMatched: boolean,
    ): AccountTreeNode | null => {
        const selfMatch = node.isMatch;
        const prunedChildren = node.children
            .map((child) => prune(child, ancestorMatched || selfMatch))
            .filter((child): child is AccountTreeNode => child !== null);

        const keep = selfMatch || ancestorMatched || prunedChildren.length > 0;
        if (!keep) {
            return null;
        }
        return { ...node, children: prunedChildren };
    };

    const prunedRoots = roots
        .map((root) => prune(root, false))
        .filter((root): root is AccountTreeNode => root !== null);

    // Auto-expand every visible group so matches are revealed in context.
    const autoExpandIds: string[] = [];
    const collectGroups = (nodes: AccountTreeNode[]) => {
        for (const node of nodes) {
            if (node.children.length > 0) {
                autoExpandIds.push(node.account.id);
                collectGroups(node.children);
            }
        }
    };
    collectGroups(prunedRoots);

    return {
        roots: prunedRoots,
        allGroupIds,
        defaultExpandedIds,
        autoExpandIds,
        visiblePostableCount: countPostable(prunedRoots),
        isSearching: true,
    };
};

/** Count postable (leaf) accounts across a node list. */
const countPostable = (nodes: AccountTreeNode[]): number =>
    nodes.reduce(
        (sum, node) =>
            sum +
            (node.account.isPostable ? 1 : 0) +
            countPostable(node.children),
        0,
    );
